import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/app-layout';
import { LoginPage } from './pages/login';
import { useAuthStore, type AdminUser, type AuthState } from './lib/stores/auth-store';
import { api, runRefresh, setAuthHandlers } from './lib/api/client';

interface RefreshResponse {
  accessToken: string;
  user: AdminUser;
}

function AdminGuard({ children }: { children: React.ReactNode }): React.ReactElement | null {
  const user = useAuthStore((s: AuthState) => s.user);
  const token = useAuthStore((s: AuthState) => s.accessToken);
  const setSession = useAuthStore((s: AuthState) => s.setSession);
  const clear = useAuthStore((s: AuthState) => s.clear);
  const [bootChecked, setBootChecked] = useState(false);

  useEffect(() => {
    setAuthHandlers({
      refresh: async () => {
        try {
          const data = await api<RefreshResponse>('/auth/refresh', {
            method: 'POST',
            _skipAuthRefresh: true,
          });
          if (data?.accessToken && data?.user) {
            setSession(data.accessToken, data.user);
            return data.accessToken;
          }
          return null;
        } catch {
          return null;
        }
      },
      onUnauthorized: () => {
        clear();
        window.location.replace('/login');
      },
    });
    return () => setAuthHandlers(null);
  }, [setSession, clear]);

  useEffect(() => {
    const currentToken = useAuthStore.getState().accessToken;
    if (currentToken) {
      setBootChecked(true);
      runRefresh().catch(() => {});
      return;
    }
    let cancelled = false;
    void (async () => {
      await runRefresh();
      if (!cancelled) setBootChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!bootChecked) return null;
  if (!token || !user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <AdminGuard>
        <AppLayout />
      </AdminGuard>
    ),
    children: [
      {
        index: true,
        lazy: async () => {
          const { DashboardPage } = await import('./pages/dashboard');
          return { Component: DashboardPage };
        },
      },
      {
        path: 'users',
        lazy: async () => {
          const { UsersPage } = await import('./pages/users');
          return { Component: UsersPage };
        },
      },
      {
        path: 'users/:id',
        lazy: async () => {
          const { UserDetailPage } = await import('./pages/user-detail');
          return { Component: UserDetailPage };
        },
      },
      {
        path: 'plans',
        lazy: async () => {
          const { PlansPage } = await import('./pages/plans');
          return { Component: PlansPage };
        },
      },
      {
        path: 'plans/:id',
        lazy: async () => {
          const { PlanDetailPage } = await import('./pages/plan-detail');
          return { Component: PlanDetailPage };
        },
      },
      {
        path: 'coupons',
        lazy: async () => {
          const { CouponsPage } = await import('./pages/coupons');
          return { Component: CouponsPage };
        },
      },
      {
        path: 'invoices',
        lazy: async () => {
          const { InvoicesPage } = await import('./pages/invoices');
          return { Component: InvoicesPage };
        },
      },
      {
        path: 'mail-logs',
        lazy: async () => {
          const { MailLogsPage } = await import('./pages/mail-logs');
          return { Component: MailLogsPage };
        },
      },
      {
        path: 'stripe-settings',
        lazy: async () => {
          const { StripeSettingsPage } = await import('./pages/stripe-settings');
          return { Component: StripeSettingsPage };
        },
      },
      {
        path: 'open-banking-settings',
        lazy: async () => {
          const { OpenBankingSettingsPage } = await import('./pages/open-banking-settings');
          return { Component: OpenBankingSettingsPage };
        },
      },
      {
        path: 'integrations-settings',
        lazy: async () => {
          const { IntegrationsSettingsPage } = await import('./pages/integrations-settings');
          return { Component: IntegrationsSettingsPage };
        },
      },
      {
        path: 'storage-settings',
        lazy: async () => {
          const { StorageSettingsPage } = await import('./pages/storage-settings');
          return { Component: StorageSettingsPage };
        },
      },
      {
        path: 'ai-settings',
        lazy: async () => {
          const { AiSettingsPage } = await import('./pages/ai-settings');
          return { Component: AiSettingsPage };
        },
      },
      {
        path: 'exchange-rates',
        lazy: async () => {
          const { ExchangeRatesPage } = await import('./pages/exchange-rates');
          return { Component: ExchangeRatesPage };
        },
      },
      {
        path: 'tags',
        lazy: async () => {
          const { TagsPage } = await import('./pages/tags');
          return { Component: TagsPage };
        },
      },
      {
        path: 'activity-logs',
        lazy: async () => {
          const { ActivityLogsPage } = await import('./pages/activity-logs');
          return { Component: ActivityLogsPage };
        },
      },
      {
        path: 'site-settings',
        lazy: async () => {
          const { SiteSettingsPage } = await import('./pages/site-settings');
          return { Component: SiteSettingsPage };
        },
      },
      {
        path: 'benefit-cards',
        lazy: async () => {
          const { BenefitCardsPage } = await import('./pages/benefit-cards');
          return { Component: BenefitCardsPage };
        },
      },
      {
        path: 'billing-metrics',
        lazy: async () => {
          const { BillingMetricsPage } = await import('./pages/billing-metrics');
          return { Component: BillingMetricsPage };
        },
      },
      {
        path: 'template-reports',
        lazy: async () => {
          const { TemplateReportsPage } = await import('./pages/template-reports');
          return { Component: TemplateReportsPage };
        },
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
